import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Briefcase, Star, Send, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { SEO } from '../components/SEO';

function StarRating({ value, onChange, label, description }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="py-3">
      <div className="flex items-center justify-between mb-1">
        <Label className="text-slate-900 font-medium">{label}</Label>
        <span className="text-sm text-slate-400">{value > 0 ? `${value}/5` : ''}</span>
      </div>
      {description && <p className="text-xs text-slate-500 mb-2">{description}</p>}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="p-0.5 transition-transform hover:scale-110"
            data-testid={`star-${label.toLowerCase().replace(/\s+/g, '-')}-${star}`}
          >
            <Star
              className={`w-8 h-8 transition-colors ${
                star <= (hover || value) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FeedbackPage() {
  const { linkId } = useParams();
  const [formInfo, setFormInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    participant_name: '',
    content_quality: 0,
    delivery: 0,
    knowledge: 0,
    engagement: 0,
    comment: ''
  });

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const res = await axios.get(`${API}/feedback/${linkId}`);
        setFormInfo(res.data);
      } catch (err) {
        setError('This feedback form is no longer available.');
      } finally {
        setLoading(false);
      }
    };
    fetchForm();
  }, [linkId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.participant_name.trim()) { toast.error('Please enter your name'); return; }
    if (form.content_quality === 0 || form.delivery === 0 || form.knowledge === 0 || form.engagement === 0) {
      toast.error('Please rate all criteria');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback/${linkId}`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Star className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-2">Form Not Available</h2>
          <p className="text-slate-500">{error}</p>
          <Link to="/" className="text-blue-600 hover:underline mt-4 inline-block text-sm">Go to Training Solutions</Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="feedback-success">
        <SEO title="Feedback Submitted" description="Thank you for your training feedback" path={`/feedback/${linkId}`} />
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="font-outfit text-3xl font-bold text-slate-900 mb-3">Thank You!</h2>
          <p className="text-slate-600 mb-6">Your feedback has been recorded. It helps trainers improve and companies make better decisions.</p>
          <Link to="/">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6">
              Visit Training Solutions
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="feedback-page">
      <SEO title="Training Feedback" description={`Rate your training experience: ${formInfo?.job_title}`} path={`/feedback/${linkId}`} />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-bold text-lg text-slate-900">Training Solutions</span>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Training Info */}
        <div className="bg-blue-50 rounded-xl border border-blue-100 p-6 mb-6">
          <p className="text-sm text-blue-600 font-medium mb-1">Training Feedback</p>
          <h1 className="font-outfit text-2xl font-bold text-slate-900 mb-1" data-testid="feedback-training-title">
            {formInfo?.job_title}
          </h1>
          <p className="text-slate-600">Trainer: <strong>{formInfo?.trainer_name}</strong></p>
        </div>

        {/* Feedback Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
          <div>
            <Label>Your Name *</Label>
            <Input
              value={form.participant_name}
              onChange={(e) => setForm(prev => ({ ...prev, participant_name: e.target.value }))}
              placeholder="Enter your full name"
              className="mt-2"
              data-testid="feedback-name-input"
            />
          </div>

          <div className="border-t border-slate-100 pt-4">
            <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-2">Rate the Training</h3>
            <p className="text-sm text-slate-500 mb-4">Please rate each aspect of the training on a scale of 1-5 stars.</p>
            
            {formInfo?.criteria?.map((c) => (
              <StarRating
                key={c.key}
                label={c.label}
                description={c.description}
                value={form[c.key]}
                onChange={(val) => setForm(prev => ({ ...prev, [c.key]: val }))}
              />
            ))}
          </div>

          <div className="border-t border-slate-100 pt-4">
            <Label>Additional Comments (Optional)</Label>
            <Textarea
              value={form.comment}
              onChange={(e) => setForm(prev => ({ ...prev, comment: e.target.value }))}
              placeholder="Share any additional thoughts about the training..."
              rows={4}
              className="mt-2"
              data-testid="feedback-comment-input"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12"
            data-testid="submit-feedback-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> Submit Feedback</>
            )}
          </Button>
        </form>
      </main>
    </div>
  );
}
